import {
  AfterViewInit,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { ModalOptions, BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";

import { EditBrandModalComponent } from "../../../../dialogs/edit-brand-modal/edit-brand-modal.component";
import { EditModelModalComponent } from "../../../../dialogs/edit-model-modal/edit-model-modal.component";
import { VehiclesHandlerService } from "../../../../api-handlers/vehicles-handler.service";

@Component({
  selector: "app-brands-and-models",
  templateUrl: "./brands-and-models.component.html",
  styleUrls: ["./brands-and-models.component.css"],
})
export class BrandsAndModelsComponent implements AfterViewInit, OnInit {
  brandErrorMessage = "";
  brandLoading = false;
  brandSubmitted = false;

  modelErrorMessage = "";
  modelLoading = false;
  modelSubmitted = false;

  brandsData = [];
  modelsData = [];

  @ViewChild("brandsPaginator") brandsPaginator: MatPaginator;
  @ViewChild("modelsPaginator") modelsPaginator: MatPaginator;

  brandsDataSource = new MatTableDataSource();
  modelsDataSource = new MatTableDataSource();

  brandsDisplayedColumns: string[] = [
    "vbr_id",
    "vbr_name",
    "vbr_closed_at",
    "vbr_created_at",
    "actions",
  ];
  modelsDisplayedColumns: string[] = [
    "vmd_id",
    "vmd_name",
    "vmd_brand_name",
    "vmd_closed_at",
    "vmd_created_at",
    "actions",
  ];

  addBrandModalRef?: BsModalRef;
  editBrandModalRef?: BsModalRef;
  newBrandForm: FormGroup = this.formBuilder.group({
    nome: [
      "",
      [Validators.required, Validators.minLength(2), Validators.maxLength(25)],
    ],
  });

  addModelModalRef?: BsModalRef;
  editModelModalRef?: BsModalRef;
  newModelForm: FormGroup = this.formBuilder.group({
    nome: [
      "",
      [Validators.required, Validators.minLength(2), Validators.maxLength(25)],
    ],
    marca: ["", [Validators.required]],
  });

  brandsFilteredOptions: Observable<any>;

  get getBrandFormControls() {
    return this.newBrandForm.controls;
  }

  get getModelFormControls() {
    return this.newModelForm.controls;
  }

  constructor(
    private formBuilder: FormBuilder,
    private modalService: BsModalService,
    private snackBar: MatSnackBar,
    private vehiclesService: VehiclesHandlerService
  ) {}

  ngOnInit() {
    this.brandsFilteredOptions = this.newModelForm.valueChanges.pipe(
      startWith(""),
      map((value) => this._filter(value.marca || ""))
    );
  }

  ngAfterViewInit(): void {
    (async () => {
      try {
        await this.getBrands();
        await this.getModels();
      } catch (error) {
        this.snackBar.open(error.message, "Chuida");
      }
    })();
  }

  private _filter(value: string) {
    const filterValue = value.toLowerCase();

    return this.brandsData.filter((option) =>
      option.vehicleModels.vbr_name.toLowerCase().includes(filterValue)
    );
  }

  getBrands = async () => {
    try {
      const getBrandsRes = await this.vehiclesService.getRawBrands();

      this.brandsData = [...getBrandsRes];
      this.brandsDataSource = new MatTableDataSource(getBrandsRes);
      this.brandsDataSource.paginator = this.brandsPaginator;

      this.brandsDataSource.filterPredicate = (data, filter: string) => {
        const accumulator = (currentTerm, key) => {
          return this.nestedFilterCheck(currentTerm, data, key);
        };
        const dataStr = Object.keys(data).reduce(accumulator, "").toLowerCase();
        const transformedFilter = filter.trim().toLowerCase();
        return dataStr.indexOf(transformedFilter) !== -1;
      };
    } catch (error) {
      this.snackBar.open(error.message, "Chuida");
    }
  };

  getModels = async () => {
    try {
      const getModelsRes = await this.vehiclesService.getRawModels();

      const temp = [...getModelsRes];

      temp.forEach((model) => {
        const foundBrandName = this.brandsData.find(
          (brand) =>
            brand.vehicleModels.vbr_id === model.vehicleModels.vmd_brand_id
        );
        model.vmd_brand_name = foundBrandName.vehicleModels.vbr_name;
      });

      this.modelsData = [...temp];
      this.modelsDataSource = new MatTableDataSource(temp);
      this.modelsDataSource.paginator = this.modelsPaginator;

      this.modelsDataSource.filterPredicate = (data, filter: string) => {
        const accumulator = (currentTerm, key) => {
          return this.nestedFilterCheck(currentTerm, data, key);
        };
        const dataStr = Object.keys(data).reduce(accumulator, "").toLowerCase();
        const transformedFilter = filter.trim().toLowerCase();
        return dataStr.indexOf(transformedFilter) !== -1;
      };
    } catch (error) {
      this.snackBar.open(error.message, "Chuida");
    }
  };

  openAddBrandModal(template: TemplateRef<any>) {
    this.newBrandForm.reset();
    this.addBrandModalRef = this.modalService.show(template, {
      ignoreBackdropClick: true,
      keyboard: false,
    });

    this.addBrandModalRef.onHide.subscribe(this.getBrands);
  }

  openAddModelModal(template: TemplateRef<any>) {
    this.newModelForm.reset();
    this.addModelModalRef = this.modalService.show(template, {
      ignoreBackdropClick: true,
      keyboard: false,
    });

    this.addModelModalRef.onHide.subscribe(this.getModels);
  }

  async onAddBrandSubmit() {
    this.brandSubmitted = true;
    this.brandErrorMessage = "";

    if (this.newBrandForm.invalid) {
      return;
    }

    this.brandLoading = true;

    try {
      let name = this.getBrandFormControls.nome.value.trim();
      name = name[0].toUpperCase() + name.slice(1).toLowerCase();

      const doesNameExist = this.brandsData.find(
        (brand) => brand.vehicleModels.vbr_name === name
      );

      if (doesNameExist) {
        throw new Error("Marca già esiste");
      }

      const createBrandBody = {
        vbr_name: name,
      };

      await this.vehiclesService.adminCreateBrand(createBrandBody);

      this.brandErrorMessage = "";
      this.brandLoading = false;
      this.brandSubmitted = false;
      this.newBrandForm.reset();
      this.snackBar.open(`Marca "${name}" creato`, undefined, {
        duration: 3000,
      });
      this.addBrandModalRef.hide();
    } catch (error) {
      this.brandErrorMessage = error.message;
      this.brandSubmitted = false;
      this.brandLoading = false;
    }
  }

  async onAddModelSubmit() {
    this.modelSubmitted = true;
    this.modelErrorMessage = "";

    if (this.newModelForm.invalid) {
      return;
    }

    this.modelLoading = true;

    try {
      const name = this.getModelFormControls.nome.value.trim();
      const selectedBrand = this.getModelFormControls.marca.value;

      const doesNameExist = this.modelsData.find(
        (model) => model?.vehicleModels.vmd_name === name
      );

      const foundBrand = this.brandsData.find(
        (b) => b.vehicleModels.vbr_name === selectedBrand
      );

      if (doesNameExist) {
        throw new Error("Modello già esiste");
      }

      if (!foundBrand) {
        throw new Error("Marca non esiste");
      }

      const createModelBody = {
        vmd_brand_id: foundBrand.vehicleModels.vbr_id,
        vmd_name: name,
      };

      await this.vehiclesService.adminCreateModel(createModelBody);

      this.modelErrorMessage = "";
      this.modelLoading = false;
      this.modelSubmitted = false;
      this.newModelForm.reset();
      this.snackBar.open(`Modello "${name}" creato`, undefined, {
        duration: 3000,
      });
      this.addModelModalRef.hide();
    } catch (error) {
      this.modelErrorMessage = error.message;
      this.modelSubmitted = false;
      this.modelLoading = false;
    }
  }

  applyBrandsSearchFilter(filterVal) {
    this.brandsDataSource.filter = filterVal.trim().toLowerCase();
  }

  applyModelsSearchFilter(filterVal) {
    this.modelsDataSource.filter = filterVal.trim().toLowerCase();
  }

  onBrandRowActionHandler(brandData) {
    const initialState: ModalOptions = {
      initialState: {
        brandId: brandData?.vehicleModels.vbr_id,
        name: brandData?.vehicleModels.vbr_name,
        closed_at: brandData?.vehicleModels.vbr_closed_at ? "1" : "0",
      },
      ignoreBackdropClick: true,
      keyboard: false,
    };

    this.editBrandModalRef = this.modalService.show(
      EditBrandModalComponent,
      initialState
    );

    this.editBrandModalRef.onHide.subscribe(this.getBrands);
  }

  onModelRowActionHandler(modelData) {
    const initialState: ModalOptions = {
      initialState: {
        modelId: modelData?.vehicleModels.vmd_id,
        name: modelData?.vehicleModels.vmd_name,
        closed_at: modelData?.vehicleModels.vmd_closed_at ? "1" : "0",
        selectedBrandName: modelData.vmd_brand_name,
        brandsData: this.brandsData,
        modelsData: this.modelsData,
      },
      ignoreBackdropClick: true,
      keyboard: false,
    };

    this.editModelModalRef = this.modalService.show(
      EditModelModalComponent,
      initialState
    );

    this.editModelModalRef.onHide.subscribe(this.getModels);
  }

  nestedFilterCheck(search, data, key) {
    if (typeof data[key] === "object") {
      for (const k in data[key]) {
        if (data[key][k] !== null) {
          search = this.nestedFilterCheck(search, data[key], k);
        }
      }
    } else {
      search += data[key];
    }
    return search;
  }
}
